import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { API_URL, formatDate, getStatusBadgeClass } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Users,
  FileText,
  Loader2,
  Building2,
  UserCheck,
  TrendingUp,
  DollarSign,
  Settings,
  CreditCard,
  Shield,
  Upload,
  Rss,
  FileSpreadsheet,
  Download,
  CheckCircle2,
  AlertCircle,
  BarChart3,
  Mail,
  MessageSquare,
  MousePointerClick,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Send,
  Clock,
  BadgeCheck,
  ExternalLink,
  XCircle,
  MapPin,
  Bell,
  Eye,
} from 'lucide-react';
import { useAdminCtx } from './AdminContext';


const AdminTabDigest = () => {
  const { digestHistory, digestSettings, handleSaveDigestSettings, handleSendDigestNow, handleUpdateSchedule, savingDigest, scheduleTime, schedulerStatus, sendingDigest, setDigestSettings, setScheduleTime, users } = useAdminCtx();
  return (
    <>
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Digest Settings */}
              <div className="bg-white rounded-xl p-6 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">Digest Settings</h3>
                    <p className="text-sm text-gray-500">Configure email digest notifications</p>
                  </div>
                </div>

                {digestSettings ? (
                  <div className="space-y-5">
                    {/* Enable/Disable */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">Enable Digests</p>
                        <p className="text-sm text-gray-500">Receive periodic email summaries</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={digestSettings.enabled}
                          onChange={(e) => setDigestSettings({ ...digestSettings, enabled: e.target.checked })}
                          className="sr-only peer"
                          data-testid="digest-enabled-toggle"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                      </label>
                    </div>

                    {/* Frequency */}
                    <div>
                      <Label>Frequency</Label>
                      <select
                        value={digestSettings.frequency}
                        onChange={(e) => setDigestSettings({ ...digestSettings, frequency: e.target.value })}
                        className="w-full mt-1 border rounded-lg px-3 py-2"
                        data-testid="digest-frequency-select"
                      >
                        <option value="daily">Daily</option>
                        <option value="weekly">Weekly (Mondays)</option>
                      </select>
                    </div>

                    {/* Schedule Time */}
                    <div>
                      <Label>Send Time (UTC)</Label>
                      <div className="flex gap-2 mt-1">
                        <select
                          value={scheduleTime.hour}
                          onChange={(e) => setScheduleTime({ ...scheduleTime, hour: parseInt(e.target.value) })}
                          className="flex-1 border rounded-lg px-3 py-2"
                          data-testid="digest-hour-select"
                        >
                          {Array.from({ length: 24 }, (_, i) => (
                            <option key={i} value={i}>{i.toString().padStart(2, '0')}:00</option>
                          ))}
                        </select>
                        <Button
                          variant="outline"
                          onClick={handleUpdateSchedule}
                          className="px-4"
                          data-testid="update-schedule-btn"
                        >
                          <Clock className="w-4 h-4" />
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Currently scheduled for {scheduleTime.hour.toString().padStart(2, '0')}:00 UTC
                      </p>
                    </div>

                    {/* Include Options */}
                    <div className="space-y-3">
                      <Label>Include in Digest</Label>
                      <div className="space-y-2">
                        {[
                          { key: 'include_analytics', label: 'Contact Analytics' },
                          { key: 'include_pending_messages', label: 'Pending Messages' },
                          { key: 'include_new_users', label: 'New User Registrations' },
                          { key: 'include_new_jobs', label: 'New Jobs & Applications' },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={digestSettings[key]}
                              onChange={(e) => setDigestSettings({ ...digestSettings, [key]: e.target.checked })}
                              className="w-4 h-4 text-primary rounded"
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>

                    {/* Recipient Emails */}
                    <div>
                      <Label>Recipient Emails (comma-separated)</Label>
                      <Input
                        value={(digestSettings.recipient_emails || []).join(', ')}
                        onChange={(e) => setDigestSettings({
                          ...digestSettings,
                          recipient_emails: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                        })}
                        placeholder="admin@example.com, support@example.com"
                        className="mt-1"
                        data-testid="digest-emails-input"
                      />
                    </div>

                    {/* Marketplace Emails (weekly automated) */}
                    <div className="space-y-3" data-testid="marketplace-emails-section">
                      <Label>Marketplace Emails (automated weekly)</Label>
                      <div className="space-y-2">
                        {[
                          { key: 'weekly_employer_applicant_digest', label: 'Employer: "New applicants this week" (Mon)' },
                          { key: 'weekly_jobseeker_job_digest', label: 'Job Seeker: "New jobs matching your license" (Thu)' },
                        ].map(({ key, label }) => (
                          <label key={key} className="flex items-center gap-3 p-2 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={digestSettings[key] !== false}
                              onChange={(e) => setDigestSettings({ ...digestSettings, [key]: e.target.checked })}
                              className="w-4 h-4 text-primary rounded"
                              data-testid={`toggle-${key}`}
                            />
                            <span className="text-sm text-gray-700">{label}</span>
                          </label>
                        ))}
                      </div>
                    </div>


                    <Button
                      onClick={handleSaveDigestSettings}
                      disabled={savingDigest}
                      className="w-full bg-primary hover:bg-primary-hover text-white"
                      data-testid="save-digest-settings-btn"
                    >
                      {savingDigest ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        'Save Settings'
                      )}
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                )}
              </div>

              {/* Send Now & History */}
              <div className="space-y-6">
                {/* Scheduler Status */}
                {schedulerStatus && (
                  <div className={`rounded-xl p-4 border ${schedulerStatus.scheduler_running ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${schedulerStatus.scheduler_running ? 'bg-emerald-500 animate-pulse' : 'bg-red-500'}`} />
                        <div>
                          <p className="font-medium text-gray-900">
                            Automated Scheduler: {schedulerStatus.scheduler_running ? 'Active' : 'Inactive'}
                          </p>
                          {schedulerStatus.next_scheduled_run && (
                            <p className="text-xs text-gray-500">
                              Next run: {new Date(schedulerStatus.next_scheduled_run).toLocaleString()}
                            </p>
                          )}
                        </div>
                      </div>
                      {schedulerStatus.last_digest_sent && (
                        <div className="text-right text-xs text-gray-500">
                          <p>Last sent: {formatDate(schedulerStatus.last_digest_sent)}</p>
                          <p className="capitalize">{schedulerStatus.last_digest_period} digest</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Send Now */}
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                      <Send className="w-5 h-5 text-emerald-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Send Digest Now</h3>
                      <p className="text-sm text-gray-500">Manually trigger a digest email</p>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={() => handleSendDigestNow('daily')}
                      disabled={sendingDigest}
                      variant="outline"
                      className="flex-1"
                      data-testid="send-daily-digest-btn"
                    >
                      {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Daily Digest'}
                    </Button>
                    <Button
                      onClick={() => handleSendDigestNow('weekly')}
                      disabled={sendingDigest}
                      variant="outline"
                      className="flex-1"
                      data-testid="send-weekly-digest-btn"
                    >
                      {sendingDigest ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Weekly Digest'}
                    </Button>
                  </div>
                </div>

                {/* Digest History */}
                <div className="bg-white rounded-xl p-6 border border-gray-100">
                  <h3 className="font-semibold text-gray-900 mb-4">Recent Digest History</h3>
                  {digestHistory.length > 0 ? (
                    <div className="space-y-3">
                      {digestHistory.map((log) => (
                        <div key={log.digest_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900 capitalize">{log.period} Digest</p>
                            <p className="text-xs text-gray-500">{formatDate(log.sent_at)}</p>
                          </div>
                          <div className="text-right text-xs text-gray-500">
                            <p>{log.data_summary?.contact_forms || 0} forms</p>
                            <p>{log.data_summary?.new_users || 0} users</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No digests sent yet</p>
                  )}
                </div>
              </div>
            </div>
    </>
  );
};

export default AdminTabDigest;
